import {
  getApiHeaders,
  logout,
  hasValidJwt,
  isPublicAuthUrl,
  isRefreshSessionUrl,
  updateSessionToken,
  getJwtTimeRemainingMs,
} from './authService';
import { markSessionRefreshBaseline, sessionRefreshState } from './sessionRefreshState';

const REFRESH_SESSION_URL = 'api/User/RefreshSession';
const REFRESH_THRESHOLD_MS = 3 * 60 * 1000;
const REFRESH_MIN_INTERVAL_MS = 60 * 1000;

export { markSessionRefreshBaseline };

export async function parseApiResponse(response) {
  const text = await response.text();

  if (text === 'Timeout') {
    logout();
    window.location.href = '/';
    return 'Timeout';
  }

  if (text === 'Forbidden.' || response.status === 403) {
    return 'Forbidden.';
  }

  try {
    return normalizeApiPayload(JSON.parse(text));
  } catch {
    return text;
  }
}

function normalizeApiPayload(data) {
  if (!data || typeof data !== 'object' || !data.errorMessage) {
    return data;
  }

  if (data.isBusinessError === false && data.requestId) {
    return {
      ...data,
      errorMessage: `Ocurrió un error técnico. Comuníquese con administración e indique el código: ${data.requestId}`,
    };
  }

  return data;
}

function shouldRefreshSession() {
  if (!hasValidJwt()) {
    return false;
  }

  const remaining = getJwtTimeRemainingMs();
  if (remaining <= REFRESH_THRESHOLD_MS) {
    return true;
  }

  // Sliding proactivo mientras la sesión sea válida (actividad / watchdog).
  return Date.now() - sessionRefreshState.lastRefreshAt >= REFRESH_MIN_INTERVAL_MS;
}

export async function refreshSession() {
  if (!hasValidJwt()) {
    return false;
  }

  const response = await fetch(REFRESH_SESSION_URL, {
    method: 'POST',
    headers: getApiHeaders(),
  });
  const data = await parseApiResponse(response);

  if (data === 'Forbidden.' || data === 'Timeout' || isApiError(data)) {
    return false;
  }

  const model = data?.returnModel;
  if (!model?.token) {
    return false;
  }

  updateSessionToken(model.token, model.expiresAtUtc);
  sessionRefreshState.lastRefreshAt = Date.now();
  return true;
}

export async function refreshSessionIfNeeded() {
  if (!shouldRefreshSession()) {
    return true;
  }

  if (!sessionRefreshState.refreshInFlight) {
    sessionRefreshState.refreshInFlight = refreshSession().finally(() => {
      sessionRefreshState.refreshInFlight = null;
    });
  }

  return sessionRefreshState.refreshInFlight;
}

export async function apiFetch(url, options = {}) {
  const publicUrl = isPublicAuthUrl(url);
  const refreshUrl = isRefreshSessionUrl(url);

  if (!publicUrl && !refreshUrl) {
    await refreshSessionIfNeeded();
    if (!hasValidJwt()) {
      logout();
      window.location.href = '/';
      return { response: { status: 403, ok: false }, data: 'Forbidden.' };
    }
  } else if (refreshUrl && !hasValidJwt()) {
    logout();
    window.location.href = '/';
    return { response: { status: 403, ok: false }, data: 'Forbidden.' };
  }

  const { headers: extraHeaders, ...rest } = options;
  const response = await fetch(url, {
    ...rest,
    headers: getApiHeaders(extraHeaders),
  });
  const data = await parseApiResponse(response);
  return { response, data };
}

export function isApiError(data) {
  return data === 'Forbidden.' || data === 'Timeout' || Boolean(data && data.errorMessage);
}

export function getApiErrorMessage(data, fallback = 'Forbidden.') {
  const normalized = normalizeApiPayload(data);
  if (normalized && normalized.errorMessage) {
    return normalized.errorMessage;
  }
  if (data === 'Forbidden.') {
    return fallback;
  }
  return '';
}
